import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, ArrowLeft, Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { login, completeLogin2fa } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  // Step 2 state — set after a successful password+email when 2FA is enabled
  const [partialToken, setPartialToken] = useState(null);
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const codeInputRef = useRef(null);

  useEffect(() => {
    if (partialToken) codeInputRef.current?.focus();
  }, [partialToken]);

  const goHome = (u) =>
    nav(u?.is_admin ? "/admin" : u?.is_fortinet_reviewer ? "/reviews" : "/configurator");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(email, pw);
      if (result.requires2fa) {
        setPartialToken(result.partialToken);
        toast.info("Saisissez le code à 6 chiffres généré par votre application d'authentification.");
      } else {
        toast.success("Bon retour parmi nous");
        goHome(result.user);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de la connexion");
    } finally {
      setLoading(false);
    }
  };

  const verify2fa = async (e) => {
    e.preventDefault();
    setVerifying(true);
    try {
      const u = await completeLogin2fa(partialToken, code);
      toast.success("Bon retour parmi nous");
      goHome(u);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Code invalide");
      setCode("");
      codeInputRef.current?.focus();
    } finally {
      setVerifying(false);
    }
  };

  const cancel2fa = () => {
    setPartialToken(null);
    setCode("");
  };

  return (
    <div className="min-h-[calc(100vh-56px)] flex items-center justify-center px-4 bg-[#F8F9FA]">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 justify-center mb-8">
          <img src="/pythagore-logo.svg" alt="Pythagore" className="h-9" />
        </div>
        <div className="bg-white border border-zinc-200 rounded-sm p-8">
          {!partialToken ? (
            <>
              <h1 className="text-2xl font-semibold tracking-tight text-zinc-900">Se connecter</h1>
              <p className="mt-1 text-sm text-zinc-600">Accédez à vos devis enregistrés et à l'assistant IA.</p>
              <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wide text-zinc-700">Email</Label>
                  <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 rounded-sm" data-testid="login-email-input" />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wide text-zinc-700">Mot de passe</Label>
                  <div className="relative mt-1">
                    <Input
                      type={showPw ? "text" : "password"}
                      required
                      value={pw}
                      onChange={(e) => setPw(e.target.value)}
                      className="rounded-sm pr-10"
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw((v) => !v)}
                      className="absolute inset-y-0 right-0 h-full w-10 inline-flex items-center justify-center text-zinc-400 hover:text-zinc-700 focus:outline-none"
                      aria-label={showPw ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                      aria-pressed={showPw}
                      title={showPw ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                      data-testid="login-password-toggle"
                    >
                      {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-[#D42B1E] hover:bg-[#B32218] rounded-sm h-11" data-testid="login-submit-button">
                  {loading ? "Connexion..." : "Se connecter"}
                </Button>
                <div className="text-right">
                  <Link
                    to="/forgot-password"
                    className="text-[12px] text-zinc-500 hover:text-[#D42B1E] hover:underline"
                    data-testid="login-forgot-password-link"
                  >
                    Mot de passe oublié ?
                  </Link>
                </div>
              </form>
              <p className="mt-6 text-sm text-center text-zinc-600">
                Pas encore de compte ? <Link to="/register" className="text-[#D42B1E] font-medium" data-testid="login-register-link">Créer un compte</Link>
              </p>
            </>
          ) : (
            <div data-testid="login-2fa-step">
              <div className="flex items-center gap-2 text-[#D42B1E]">
                <ShieldCheck className="h-5 w-5" />
                <span className="text-[10px] font-semibold tracking-[0.2em] uppercase">Vérification en deux étapes</span>
              </div>
              <h1 className="mt-2 text-2xl font-semibold tracking-tight text-zinc-900">Code à 6 chiffres</h1>
              <p className="mt-1 text-sm text-zinc-600">
                Ouvrez votre application d'authentification (Google Authenticator, Authy, 1Password…) et saisissez le code en cours pour <span className="font-medium text-zinc-800">{email}</span>.
              </p>
              <form onSubmit={verify2fa} className="mt-6 space-y-4">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wide text-zinc-700">Code TOTP</Label>
                  <Input
                    ref={codeInputRef}
                    type="text"
                    inputMode="numeric"
                    pattern="\d{6}"
                    maxLength={6}
                    autoComplete="one-time-code"
                    required
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    className="mt-1 rounded-sm font-mono text-2xl tracking-[0.4em] text-center"
                    placeholder="••••••"
                    data-testid="login-2fa-code-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={verifying || code.length !== 6}
                  className="w-full bg-[#D42B1E] hover:bg-[#B32218] rounded-sm h-11"
                  data-testid="login-2fa-submit-button"
                >
                  {verifying ? "Vérification..." : "Vérifier le code"}
                </Button>
                <button
                  type="button"
                  onClick={cancel2fa}
                  className="w-full text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center justify-center gap-1.5"
                  data-testid="login-2fa-cancel"
                >
                  <ArrowLeft className="h-3 w-3" /> Revenir à la connexion
                </button>
              </form>
              <p className="mt-6 text-[11px] text-zinc-500 leading-relaxed text-center">
                Téléphone perdu ? Contactez un administrateur Pythagore pour réinitialiser votre 2FA.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
